import { useEffect, useRef } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import { COMPANY_ACTION_QUERY_PARAM, type CompanyPageAction, isCompanyPageAction } from 'common/constants/pageActions';

// Hosts register undefined while loading, false when unavailable, or a callback
// when ready; they restore undefined during cleanup.
export type CompanyPageActionHandler = (() => void) | false | undefined;
export type CompanyPageActionHandlers = Partial<Record<CompanyPageAction, CompanyPageActionHandler>>;

interface UseCompanyPageActionParams {
  handlers: CompanyPageActionHandlers;
}

const useCompanyPageAction = ({ handlers }: UseCompanyPageActionParams) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const consumedActionRef = useRef<string | null>(null);
  const action = searchParams.get(COMPANY_ACTION_QUERY_PARAM);

  // Wait for the host page to resolve availability, then consume the transient action once.
  useEffect(() => {
    if (!action) {
      consumedActionRef.current = null;
      return;
    }

    const clearAction = () => {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete(COMPANY_ACTION_QUERY_PARAM);
      setSearchParams(nextSearchParams, { replace: true });
    };

    if (!isCompanyPageAction(action) || !Object.hasOwn(handlers, action)) {
      consumedActionRef.current = action;
      clearAction();
      return;
    }

    const handler = handlers[action];
    if (handler === undefined) {
      return;
    }

    // A handler may update another query param from the pre-consumption URL and
    // momentarily restore the action. Remove it again without executing twice.
    if (consumedActionRef.current === action) {
      clearAction();
      return;
    }

    consumedActionRef.current = action;
    clearAction();

    if (handler === false) {
      enqueueSnackbar('This action is not available in the current context.', { variant: 'info' });
      return;
    }

    handler();
  }, [action, handlers, searchParams, setSearchParams]);
};

export default useCompanyPageAction;
